import Checkbox from 'components/Checkbox/checkbox'
import { useModalDepth } from 'components/UI/Modal/ModalPrimitive'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Search, XCircle } from 'lucide-react'
import { getDropdownZIndex } from 'styles/z-index'

/**
 * Hook to dismiss a dropdown on click outside or Escape key.
 * Attaches document listeners only while the dropdown is open.
 */
export function useDropdownDismiss(
  containerRef: React.RefObject<HTMLElement | null>,
  open: boolean,
  onClose: () => void
): void {
  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open, containerRef, onClose])

  useEffect(() => {
    if (!open) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, onClose])
}

export interface SectionedItem<T = string> {
  label: string
  value: T
}

export interface SectionedSection<T = string> {
  key: string
  items: SectionedItem<T>[]
}

export interface SectionedMultiSelectProps<T = string> {
  /** Label shown before the colon on the trigger button. */
  buttonLabel: string
  /** Sections of checkbox items separated by dividers. */
  sections: SectionedSection<T>[]
  /** Currently selected values across all sections. */
  selected: T[]
  /** Called with new selection when user clicks Apply. */
  onApply: (selected: T[]) => void
  /** Placeholder text for the search input. */
  searchPlaceholder?: string
  /** Text shown on the trigger when nothing is selected. */
  emptyLabel?: string
  /** Format the button display text from the current selection. */
  formatLabel?: (selected: T[], sections: SectionedSection<T>[]) => string
  /** Whether to show the search input. Defaults to true. */
  searchable?: boolean
  /** Width class for the dropdown panel. Defaults to "w-72". */
  dropdownWidth?: string
  /** When true, the trigger button always renders the empty/idle state
   *  (button label + chevron) regardless of whether anything is selected.
   *  Use when the surrounding page surfaces the selection elsewhere (e.g.
   *  as filter chips below the toolbar). */
  hideSelectionInTrigger?: boolean
  /** Which side of the trigger the dropdown menu aligns to. Defaults to
   *  'left'. Set to 'right' for triggers near the right edge of the
   *  viewport so the menu doesn't get clipped. */
  align?: 'left' | 'right'
}

/**
 * Multi-select dropdown with sectioned checkbox groups, search, and Apply button.
 *
 * Sections are separated by dividers. Selections are staged locally
 * and only committed when the user clicks "Apply".
 */
export function SectionedMultiSelect<T = string>({
  buttonLabel,
  sections,
  selected,
  onApply,
  searchPlaceholder = 'Search...',
  emptyLabel = 'All',
  formatLabel,
  searchable = true,
  dropdownWidth = 'w-72',
  hideSelectionInTrigger = false,
  align = 'left',
}: SectionedMultiSelectProps<T>) {
  const isActive = !hideSelectionInTrigger && selected.length > 0
  const containerRef = useRef<HTMLDivElement>(null)
  const modalDepth = useModalDepth()
  const zIndex = React.useMemo(
    () => getDropdownZIndex(modalDepth),
    [modalDepth]
  )
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [localSelected, setLocalSelected] = useState<T[]>(selected)

  const allValues = useMemo(
    () => sections.flatMap((s) => s.items.map((i) => i.value)),
    [sections]
  )

  // Sync local state when dropdown opens
  useEffect(() => {
    if (open) {
      setLocalSelected(selected)
      setSearch('')
    }
  }, [open, selected])

  const closeDropdown = useCallback(() => setOpen(false), [])
  useDropdownDismiss(containerRef, open, closeDropdown)

  const toggleValue = useCallback((value: T) => {
    setLocalSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    )
  }, [])

  const handleSelectAll = useCallback(() => {
    setLocalSelected(allValues)
  }, [allValues])

  const handleSelectNone = useCallback(() => {
    setLocalSelected([])
  }, [])

  const handleApply = useCallback(() => {
    onApply(localSelected)
    setOpen(false)
  }, [localSelected, onApply])

  /** Filter items within each section by search query. */
  const filteredSections = useMemo(() => {
    if (!search.trim()) return sections
    const q = search.toLowerCase()
    return sections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) =>
          String(item.label).toLowerCase().includes(q)
        ),
      }))
      .filter((section) => section.items.length > 0)
  }, [sections, search])

  const displayLabel = formatLabel
    ? formatLabel(selected, sections)
    : selected.length > 0
      ? `${selected.length} selected`
      : emptyLabel

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onApply([])
    },
    [onApply]
  )

  return (
    <div ref={containerRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={
          isActive
            ? 'inline-flex items-center gap-1 h-8 pl-2.5 pr-3 rounded-md bg-surface-active text-sm text-brand-active whitespace-nowrap'
            : 'inline-flex items-center gap-1 h-8 pl-2.5 pr-3 rounded-md border border-line bg-surface text-sm font-medium text-gray-700 whitespace-nowrap transition-shadow hover:shadow-[0px_1px_4px_rgba(12,12,13,0.08)]'
        }
      >
        {isActive ? (
          <>
            <span>
              {buttonLabel}:{' '}
              <span className="font-semibold">{displayLabel}</span>
            </span>
            <XCircle
              className="size-4 text-brand-active flex-shrink-0 cursor-pointer"
              onClick={handleClear}
            />
          </>
        ) : (
          <>
            <span>{emptyLabel}</span>
            <ChevronDown className="size-3.5 flex-shrink-0" />
          </>
        )}
      </button>

      {open && (
        <div
          className={`absolute top-full ${align === 'right' ? 'right-0' : 'left-0'} mt-1 ${dropdownWidth} rounded-md border border-line bg-surface shadow-md`}
          style={{ zIndex }}
        >
          {/* Search input */}
          {searchable && (
            <div className="flex items-center gap-2 px-3 py-2 border-b border-line">
              <Search className="size-4 text-fg-muted flex-shrink-0" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="flex-1 text-sm outline-none bg-transparent placeholder:text-fg-muted"
                autoFocus
              />
            </div>
          )}

          {/* Sections */}
          <div className="max-h-64 overflow-y-auto">
            {filteredSections.map((section, idx) => (
              <React.Fragment key={section.key}>
                {idx > 0 && <div className="border-t border-line" />}
                <div className="px-3 py-2 flex flex-col gap-1.5">
                  {section.items.map((item) => (
                    <label
                      key={String(item.value)}
                      className="flex items-center gap-2 cursor-pointer text-sm py-0.5"
                    >
                      <Checkbox
                        checked={localSelected.includes(item.value)}
                        onChange={() => toggleValue(item.value)}
                      />
                      <span className="truncate">{item.label}</span>
                    </label>
                  ))}
                </div>
              </React.Fragment>
            ))}
            {filteredSections.every((s) => s.items.length === 0) && (
              <div className="text-sm text-fg-secondary text-center py-3">
                No items found
              </div>
            )}
          </div>

          {/* Footer: All / None row + Apply button */}
          <div className="border-t border-line px-3 py-2 flex flex-col gap-2">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleSelectAll}
                className="flex-1 h-8 rounded-md border border-line-focus bg-surface-hover text-sm font-medium text-brand-hover hover:bg-surface-hover-deep transition-colors"
              >
                All
              </button>
              <button
                type="button"
                onClick={handleSelectNone}
                className="flex-1 h-8 rounded-md border border-line bg-surface text-sm font-medium text-fg-secondary hover:bg-surface-hover transition-colors"
              >
                None
              </button>
            </div>
            <button
              type="button"
              onClick={handleApply}
              className="w-full h-8 rounded-md bg-brand text-base font-medium text-white hover:bg-brand-pressed transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
